import { ChangeEvent, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { CustomButton } from '../../../../../shared/uiComponents';
import { UploadIcon } from '../../../../../shared/Assets/svg';
import { Download } from '../../../../../shared/Assets/svgIcons';
import { useScreenWidth } from '../../../../../shared/Helpers/hooks';
import { downloadFile } from '../../../../Reports/helpers';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { downloadTargetsTamplate, uploadTargets } from '../../../../../redux/State/clientSlice/programTargetSlice';
import { getSkillAreas } from '../../../../../redux/State/clientSlice/skillAreaSlice';

const UploadTargets = ({ funderId }: { funderId?: string | null }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const inputRef = useRef<HTMLInputElement | null>(null);

	const loading = useSelector((state) => state.programTarget.loading);

	const uploadXLSXFile = ({ target }: ChangeEvent<HTMLInputElement>) => {
		if (!target) return;
		const { files } = target;
		if (!files) return;
		const file = files[0];
		if (!file || !clientId) return;

		dispatch(uploadTargets({ file, clientId, funderId }))
			.then(unwrapResult)
			.then(() => {
				dispatch(getSkillAreas({ clientId, funderId }));
				toast('Targets uploaded successfully.');
			})
			.catch(errorNormalizer)
			.finally(() => {
				target.value = ''; // enable reupload same file
			});
	};

	const onUploadClick = () => {
		if (!inputRef?.current) return;
		inputRef.current.click();
	};

	const onDownloadClick = () => {
		dispatch(downloadTargetsTamplate())
			.then(unwrapResult)
			.then((response) => downloadFile(response))
			.catch(errorNormalizer);
	};

	return (
		<>
			<input
				ref={inputRef}
				type="file"
				accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
				id="upload-excel-file"
				onChange={uploadXLSXFile}
				formEncType="multipart/form-data"
			/>

			{isMobile ? (
				<>
					<CustomButton buttonType="secondaryGray" iconOnly={<Download />} onClick={onDownloadClick} sizeType="sm" />
					<CustomButton
						buttonType="secondaryGray"
						iconOnly={<UploadIcon />}
						onClick={onUploadClick}
						loading={loading}
						sizeType="sm"
					/>
				</>
			) : (
				<>
					<CustomButton
						buttonType="secondaryGray"
						startIcon={<Download />}
						title="Download template"
						onClick={onDownloadClick}
					/>
					<CustomButton
						buttonType="secondary"
						startIcon={<UploadIcon />}
						title="Upload Excel file"
						onClick={onUploadClick}
						loading={loading}
					/>
				</>
			)}
		</>
	);
};

export default UploadTargets;
